import { useEffect, useState } from "react";
import { Link as RouterLink, useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Container,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Typography,
} from "@mui/material";
import { Android, Close, Menu as MenuIcon } from "@mui/icons-material";
import { PLAY_STORE_URL } from "../config/store";
import { trackEvent } from "../utils/analytics";
import BrandMark from "./BrandMark";

const NAV_LINKS = [
  { label: "How It Works", hash: "#how-it-works" },
  { label: "Features", hash: "#features" },
  { label: "For Students", hash: "#students" },
  { label: "For Professionals", hash: "#professionals" },
  { label: "Blog", to: "/blog" },
];

const Header = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const on = () => setScrolled(window.scrollY > 8);
    on();
    window.addEventListener("scroll", on, { passive: true });
    return () => window.removeEventListener("scroll", on);
  }, []);
  const location = useLocation();
  const navigate = useNavigate();

  const goToSection = (hash) => {
    setOpen(false);
    if (location.pathname !== "/") {
      navigate(`/${hash}`);
      return;
    }
    const id = hash.replace("#", "");
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
      window.history.replaceState(null, "", hash);
    }
  };

  return (
    <Box
      component="header"
      sx={{
        position: "sticky",
        top: 0,
        zIndex: 20,
        bgcolor: scrolled ? "rgba(255,255,255,0.72)" : "rgba(255,255,255,0.9)",
        backdropFilter: "blur(20px) saturate(1.8)",
        WebkitBackdropFilter: "blur(20px) saturate(1.8)",
        borderBottom: "1px solid",
        borderColor: scrolled ? "rgba(94,53,177,0.12)" : "rgba(0,0,0,0.05)",
        boxShadow: scrolled ? "0 8px 30px rgba(94,53,177,0.10)" : "none",
        transition: "background-color .3s, box-shadow .3s, border-color .3s",
        px: 2,
        py: 1.25,
      }}
    >
      <Container
        maxWidth="lg"
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
        }}
      >
        <Box
          component={RouterLink}
          to="/"
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            textDecoration: "none",
            color: "primary.main",
            "&:hover": { opacity: 0.9 },
          }}
        >
          <BrandMark size={36} />
          <Typography variant="h6" fontWeight={800} sx={{ color: "inherit", lineHeight: 1 }}>
            Treasure
          </Typography>
        </Box>

        <Box
          sx={{
            display: { xs: "none", md: "flex" },
            alignItems: "center",
            gap: 2.5,
          }}
        >
          {NAV_LINKS.map((item) =>
            item.to ? (
              <Typography
                key={item.to}
                component={RouterLink}
                to={item.to}
                variant="body2"
                sx={{
                  color: location.pathname.startsWith(item.to) ? "primary.main" : "text.primary",
                  textDecoration: "none",
                  fontWeight: location.pathname.startsWith(item.to) ? 700 : 400,
                  "&:hover": { color: "primary.main" },
                }}
              >
                {item.label}
              </Typography>
            ) : (
              <Typography
                key={item.hash}
                component="button"
                type="button"
                onClick={() => goToSection(item.hash)}
                variant="body2"
                sx={{
                  border: 0,
                  background: "none",
                  cursor: "pointer",
                  color: "text.primary",
                  font: "inherit",
                  p: 0,
                  "&:hover": { color: "primary.main" },
                  "&:focus-visible": {
                    outline: "2px solid",
                    outlineColor: "primary.main",
                    outlineOffset: 3,
                  },
                }}
              >
                {item.label}
              </Typography>
            ),
          )}
          <Button
            component="a"
            href={PLAY_STORE_URL}
            target="_blank"
            rel="noopener noreferrer"
            variant="contained"
            size="small"
            startIcon={<Android sx={{ fontSize: "18px !important" }} />}
            onClick={() => trackEvent("header_google_play_click")}
            sx={{
              textTransform: "none",
              fontWeight: 700,
              borderRadius: '8px', bgcolor: '#5E35B1', boxShadow: 'none', '&:hover': { bgcolor: '#4527A0', boxShadow: 'none' },
              px: 2,
              whiteSpace: "nowrap",
            }}
          >
            Get Treasure
          </Button>
        </Box>

        <Box sx={{ display: { xs: "flex", md: "none" }, alignItems: "center", gap: 1 }}>
          <Button
            component="a"
            href={PLAY_STORE_URL}
            target="_blank"
            rel="noopener noreferrer"
            variant="contained"
            size="small"
            onClick={() => trackEvent("header_google_play_click")}
            sx={{
              textTransform: "none",
              fontWeight: 700,
              borderRadius: '8px', bgcolor: '#5E35B1', boxShadow: 'none', '&:hover': { bgcolor: '#4527A0', boxShadow: 'none' },
              px: 1.5,
              display: { xs: "none", sm: "inline-flex" },
            }}
          >
            Get Treasure
          </Button>
          <IconButton
            aria-label="Open menu"
            onClick={() => setOpen(true)}
            edge="end"
            sx={{ color: "primary.main" }}
          >
            <MenuIcon />
          </IconButton>
        </Box>
      </Container>

      <Drawer
        anchor="right"
        open={open}
        onClose={() => setOpen(false)}
        PaperProps={{ sx: { width: "min(320px, 88vw)", p: 1 } }}
      >
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", px: 1, py: 1 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <BrandMark size={28} />
            <Typography fontWeight={800} color="primary.main">
              Treasure
            </Typography>
          </Box>
          <IconButton aria-label="Close menu" onClick={() => setOpen(false)}>
            <Close />
          </IconButton>
        </Box>
        <List>
          {NAV_LINKS.map((item) =>
            item.to ? (
              <ListItemButton
                key={item.to}
                component={RouterLink}
                to={item.to}
                onClick={() => setOpen(false)}
              >
                <ListItemText primary={item.label} />
              </ListItemButton>
            ) : (
              <ListItemButton key={item.hash} onClick={() => goToSection(item.hash)}>
                <ListItemText primary={item.label} />
              </ListItemButton>
            ),
          )}
        </List>
        <Box sx={{ px: 2, pt: 1 }}>
          <Button
            fullWidth
            component="a"
            href={PLAY_STORE_URL}
            target="_blank"
            rel="noopener noreferrer"
            variant="contained"
            startIcon={<Android />}
            onClick={() => {
              trackEvent("header_google_play_click");
              setOpen(false);
            }}
            sx={{ textTransform: "none", fontWeight: 700, borderRadius: 999 }}
          >
            Get Treasure
          </Button>
        </Box>
      </Drawer>
    </Box>
  );
};

export default Header;
